import React, { useEffect, useState } from 'react';
import AdBanner from './AdBanner';
import { useAds } from '../../contexts/AdsContext';
import styles from './StickySideRailAds.module.css';

const FIXED_NAV_HEIGHT_PX = 60;
const AD_HEIGHT_PX = 250;
const AD_SLOT_HEIGHT_PX = 270;
const RAIL_VERTICAL_PADDING_PX = 40;

const getAnnouncementBannerHeightPx = () => {
  if (typeof window === 'undefined') return 0;

  const rootStyles = window.getComputedStyle(document.documentElement);
  const value = rootStyles.getPropertyValue('--announcement-banner-height');
  const parsedValue = Number.parseFloat(value);

  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const getStickySideRailAdCount = (showAds, areAdsBlocked) => {
  if (!showAds || areAdsBlocked || typeof window === 'undefined') return 0;

  const stickyTopOffsetPx = FIXED_NAV_HEIGHT_PX + getAnnouncementBannerHeightPx();
  const usableStickyHeightPx = Math.max(0, window.innerHeight - stickyTopOffsetPx);

  const availableStackHeightPx = usableStickyHeightPx - RAIL_VERTICAL_PADDING_PX;
  if (availableStackHeightPx < AD_HEIGHT_PX) {
    return 0;
  }

  return 1 + Math.floor((availableStackHeightPx - AD_HEIGHT_PX) / AD_SLOT_HEIGHT_PX);
};

export const useStickySideRailAdCount = () => {
  const { showAds, areAdsBlocked } = useAds();
  const [adCount, setAdCount] = useState(() => getStickySideRailAdCount(showAds, areAdsBlocked));

  useEffect(() => {
    const updateAdCount = () => {
      setAdCount(getStickySideRailAdCount(showAds, areAdsBlocked));
    };

    updateAdCount();

    if (!showAds || areAdsBlocked || typeof window === 'undefined') {
      return undefined;
    }

    window.addEventListener('resize', updateAdCount);

    let observer;
    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(updateAdCount);
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['style'],
      });
    }

    return () => {
      window.removeEventListener('resize', updateAdCount);
      observer?.disconnect();
    };
  }, [areAdsBlocked, showAds]);

  return {
    showAds: showAds && !areAdsBlocked,
    adCount,
  };
};

const StickySideRailAds = ({
  adCount: adCountProp = null,
  placementBase = 'sticky-side-rail',
  testId = 'sticky-side-rail-ads',
}) => {
  const { showAds, adCount: computedAdCount } = useStickySideRailAdCount();
  const adCount = Number.isInteger(adCountProp) ? adCountProp : computedAdCount;

  if (!showAds || adCount < 1) {
    return null;
  }

  return (
    <div className={styles.stack} data-testid={testId}>
      {Array.from({ length: adCount }).map((_, index) => (
        <div key={`${placementBase}-${index + 1}`} className={styles.slot}>
          <AdBanner
            placement={`${placementBase}-${index + 1}`}
            size="rectangle"
            minViewportWidth={0}
            testId={`${testId}-${index + 1}`}
          />
        </div>
      ))}
    </div>
  );
};

export default StickySideRailAds;
